import { useSyncExternalStore } from "react";

import { Icon } from "./Icon.tsx";
import { getTheme, serverTheme, subscribeTheme, toggleTheme } from "../shared/theme.ts";

/**
 * Light/dark toggle. Two shapes, one state:
 *
 * - `<ThemeToggle />` — the sliding-knob pill from the marketing site, fixed
 *   bottom-left. Used on the unauthenticated routes, which have no sidebar.
 * - `<ThemeToggle variant="nav" />` — the sidebar-footer row from the design
 *   handoff (`operator-app.html`): a `.nav-link` carrying `id="theme-toggle"`,
 *   which is the hook `body.nav-rail #theme-toggle{font-size:0}` uses to
 *   collapse it to an icon-only square in the 58px rail.
 *
 * State lives in shared/theme.ts, not local `useState`, so the ⌘K palette's
 * "Toggle theme" command repaints both instances instead of leaving them stale.
 */
export function ThemeToggle({ variant = "float" }: { variant?: "float" | "nav" }) {
  const theme = useSyncExternalStore(subscribeTheme, getTheme, serverTheme);
  const isDark = theme === "dark";
  const label = isDark ? "Switch to light mode" : "Switch to dark mode";

  if (variant === "nav") {
    return (
      <button
        type="button"
        id="theme-toggle"
        className="nav-link"
        onClick={() => toggleTheme()}
        aria-label={label}
        title={label}
      >
        <Icon name={isDark ? "sun" : "moon"} />
        {isDark ? "Light mode" : "Dark mode"}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={"theme-toggle " + (isDark ? "is-dark" : "is-light")}
      onClick={() => toggleTheme()}
      aria-label={label}
      title={label}
    >
      <span className="theme-toggle-track">
        <span className="theme-toggle-knob">
          <svg className="ico-sun" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
          </svg>
          <svg className="ico-moon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
          </svg>
        </span>
      </span>
      <span className="theme-toggle-label">
        <span className="lbl-light">Light</span>
        <span className="lbl-dark">Dark</span>
      </span>
    </button>
  );
}
